import { type QueryKey, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import i18next from 'i18next'

import type { ChatMessage, ChatOrigin, ChatStreamState } from '@/features/chat-stream/types'
import { myChatsKeys } from '@/features/messages/hooks/useMyChats'
import type { MyChatSummary } from '@/features/messages/types/messages.types'
import { playIntentsApi } from '@/features/play-intents/api/playIntentsApi'
import { playIntentKeys } from '@/features/play-intents/hooks/usePlayIntents'
import type {
  PlayIntentChatSummary,
  PlayIntentGroup,
} from '@/features/play-intents/types/playIntent.types'

import { messagesApi } from '../api/messagesApi'
import type { SessionDetail } from '../types/session.types'

import { sessionKeys } from './useSessions'

const POLL_INTERVAL_MS = 20_000

export const chatKeys = {
  all: ['chat'] as const,
  messages: (sessionId: number) =>
    [...chatKeys.all, 'messages', sessionId, i18next.language] as const,
  /**
   * Chat de un grupo de ganas de jugar, por la clave del GRUPO (no por mis ganas): es lo
   * que trae el evento en tiempo real, igual para todos los miembros.
   */
  groupMessages: (groupKey: string) => [...chatKeys.all, 'group', groupKey] as const,
}

/** Clave de caché de los mensajes de un chat, sea de partida o de grupo. */
export function chatMessagesKey(origin: ChatOrigin): QueryKey {
  return origin.kind === 'session'
    ? chatKeys.messages(origin.sessionId)
    : chatKeys.groupMessages(origin.groupKey)
}

/** Mismo chat (para efectos que dependen del chat abierto). */
export function chatOriginId(origin: ChatOrigin): string {
  return origin.kind === 'session' ? `s:${origin.sessionId}` : `g:${origin.intentId}`
}

/**
 * Mensajes de un chat (partida o grupo). Sondea cada 20 s solo si el tiempo real no está
 * (`POLLING_FALLBACK` o sin estado); `enabled=false` lo para al cerrar el chat.
 */
export function useChatMessagesQuery(
  origin: ChatOrigin,
  enabled: boolean,
  streamState?: ChatStreamState,
) {
  return useQuery<ChatMessage[]>({
    queryKey: chatMessagesKey(origin),
    queryFn: () =>
      origin.kind === 'session'
        ? messagesApi.list(origin.sessionId)
        : playIntentsApi.chatMessages(origin.intentId),
    enabled,
    refetchInterval:
      enabled && (streamState === 'POLLING_FALLBACK' || streamState === undefined)
        ? POLL_INTERVAL_MS
        : false,
    staleTime: 0,
  })
}

/**
 * Envía un mensaje. Optimista: inserta una versión temporal con id negativo (la cambia
 * por la real al responder). En una partida, además sube `chatMessageCount` del detalle
 * para que el botón del lateral lo refleje al momento. Rollback de ambos en error.
 * En un grupo, al confirmar se refresca la bandeja (último mensaje).
 */
export function useSendMessageMutation(
  origin: ChatOrigin,
  currentUser: { id: number; username: string },
) {
  const qc = useQueryClient()
  const key = chatMessagesKey(origin)
  const detailKey = origin.kind === 'session' ? sessionKeys.detail(origin.sessionId) : null
  return useMutation({
    mutationFn: (content: string) =>
      origin.kind === 'session'
        ? messagesApi.send(origin.sessionId, content)
        : playIntentsApi.sendChatMessage(origin.intentId, content),
    onMutate: async (content: string) => {
      await qc.cancelQueries({ queryKey: key })
      if (detailKey) await qc.cancelQueries({ queryKey: detailKey })

      const previousMessages = qc.getQueryData<ChatMessage[]>(key) ?? []
      const optimistic: ChatMessage = {
        id: -Date.now(), // id temporal negativo (no choca con reales)
        userId: currentUser.id,
        username: currentUser.username,
        content,
        createdAt: new Date().toISOString(),
      }
      qc.setQueryData<ChatMessage[]>(key, [...previousMessages, optimistic])

      // Solo en partida: chatMessageCount del detalle (null = CANCELLED/COMPLETED).
      const previousDetail = detailKey ? qc.getQueryData<SessionDetail>(detailKey) : undefined
      if (detailKey && previousDetail && previousDetail.chatMessageCount !== null) {
        qc.setQueryData<SessionDetail>(detailKey, {
          ...previousDetail,
          chatMessageCount: previousDetail.chatMessageCount + 1,
        })
      }

      return { previousMessages, previousDetail, optimisticId: optimistic.id }
    },
    onError: (_err, _content, ctx) => {
      if (!ctx) return
      qc.setQueryData(key, ctx.previousMessages)
      if (detailKey && ctx.previousDetail) {
        qc.setQueryData(detailKey, ctx.previousDetail)
      }
    },
    onSuccess: (real, _content, ctx) => {
      qc.setQueryData<ChatMessage[]>(key, (curr) => {
        const list = curr ?? []
        // Race con el SSE: si el stream ya insertó el real, solo elimina el
        // optimista — si no, reemplaza el optimista por el real (caso normal).
        if (list.some((m) => m.id === real.id)) {
          return list.filter((m) => m.id !== ctx?.optimisticId)
        }
        return list.map((m) => (m.id === ctx?.optimisticId ? real : m))
      })
      if (origin.kind === 'group') {
        void qc.invalidateQueries({ queryKey: playIntentKeys.chats() })
      }
    },
  })
}

/**
 * Marca el chat como leído. Pone optimísticamente el no leído a 0 en TODOS los caches que
 * lo pintan (patrón «mutation optimista que toca contador en otro cache» del CLAUDE.md):
 *   - partida: el detalle (`sessionKeys.detail`) y su fila de `/messages`;
 *   - grupo: su fila de `/messages` (`playIntentKeys.chats`) y la tarjeta del grupo en el
 *     listado (`playIntentKeys.allGroups`, todas las ciudades/viewers en caché).
 * Sin esto el badge se quedaba en «1 sin leer» hasta el siguiente refetch, que con el
 * tiempo real activo podía no llegar nunca.
 */
export function useMarkChatReadMutation(origin: ChatOrigin) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () =>
      origin.kind === 'session'
        ? messagesApi.markRead(origin.sessionId)
        : playIntentsApi.markChatRead(origin.intentId),
    onMutate: async () => {
      if (origin.kind === 'session') {
        const { sessionId } = origin
        await qc.cancelQueries({ queryKey: sessionKeys.detail(sessionId) })
        await qc.cancelQueries({ queryKey: myChatsKeys.list })
        const previousDetail = qc.getQueryData<SessionDetail>(sessionKeys.detail(sessionId))
        const previousList = qc.getQueryData<MyChatSummary[]>(myChatsKeys.list)
        if (previousDetail && previousDetail.chatUnreadCount !== null) {
          qc.setQueryData<SessionDetail>(sessionKeys.detail(sessionId), {
            ...previousDetail,
            chatUnreadCount: 0,
          })
        }
        if (previousList) {
          qc.setQueryData<MyChatSummary[]>(
            myChatsKeys.list,
            previousList.map((c) => (c.sessionId === sessionId ? { ...c, chatUnreadCount: 0 } : c)),
          )
        }
        return { restore: [] as [QueryKey, unknown][], previousDetail, previousList }
      }

      const { intentId } = origin
      await qc.cancelQueries({ queryKey: playIntentKeys.chats() })
      await qc.cancelQueries({ queryKey: playIntentKeys.allGroups() })
      const restore: [QueryKey, unknown][] = [
        [playIntentKeys.chats(), qc.getQueryData(playIntentKeys.chats())],
        ...qc.getQueriesData({ queryKey: playIntentKeys.allGroups() }),
      ]
      qc.setQueryData<PlayIntentChatSummary[]>(playIntentKeys.chats(), (list) =>
        list?.map((c) => (c.intentId === intentId ? { ...c, chatUnreadCount: 0 } : c)),
      )
      qc.setQueriesData<PlayIntentGroup[]>({ queryKey: playIntentKeys.allGroups() }, (groups) =>
        groups?.map((g) => (g.myIntentId === intentId ? { ...g, chatUnreadCount: 0 } : g)),
      )
      return { restore, previousDetail: undefined, previousList: undefined }
    },
    onError: (_err, _vars, ctx) => {
      if (!ctx) return
      if (origin.kind === 'session') {
        if (ctx.previousDetail)
          qc.setQueryData(sessionKeys.detail(origin.sessionId), ctx.previousDetail)
        if (ctx.previousList) qc.setQueryData(myChatsKeys.list, ctx.previousList)
        return
      }
      for (const [k, data] of ctx.restore) qc.setQueryData(k, data)
    },
  })
}
